import React, { useState } from "react";
import axios from "axios";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", phone: "", email: "", service: "", message: "" };

export default function ContactForm({ compact = false }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Please fill in name, phone, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        service: form.service || null,
        message: form.message,
      });
      setSuccess(true);
      setForm(initial);
      toast.success("Thanks! We'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Could not send your message. Please call us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="contact-form-success"
        className="bg-white border border-slate-200 p-8 lg:p-10 text-center"
      >
        <CheckCircle2 className="h-12 w-12 mx-auto text-[#0284C7]" strokeWidth={1.5} />
        <h3 className="font-display text-2xl font-bold mt-4 text-[#0F172A]">
          Message Received
        </h3>
        <p className="text-slate-600 mt-2">
          Thanks for reaching out. A team member will contact you shortly. For
          emergencies, please call us directly.
        </p>
        <Button
          data-testid="contact-form-send-another-button"
          onClick={() => setSuccess(false)}
          className="mt-6 rounded-none bg-[#0284C7] hover:bg-[#0369A1] text-white"
        >
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className={`bg-white border border-slate-200 ${compact ? "p-6" : "p-6 lg:p-10"} space-y-5`}
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="contact-name" className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569]">
            Name <span className="text-[#DC2626]">*</span>
          </Label>
          <Input
            id="contact-name"
            data-testid="contact-name-input"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="John Doe"
            className="rounded-none border-slate-300 focus-visible:ring-[#0284C7] h-11"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-phone" className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569]">
            Phone <span className="text-[#DC2626]">*</span>
          </Label>
          <Input
            id="contact-phone"
            data-testid="contact-phone-input"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(781) 555-0100"
            className="rounded-none border-slate-300 focus-visible:ring-[#0284C7] h-11"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="contact-email" className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569]">
            Email <span className="text-[#DC2626]">*</span>
          </Label>
          <Input
            id="contact-email"
            data-testid="contact-email-input"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@email.com"
            className="rounded-none border-slate-300 focus-visible:ring-[#0284C7] h-11"
            required
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569]">
            Service Needed
          </Label>
          <Select value={form.service} onValueChange={(v) => update("service", v)}>
            <SelectTrigger
              data-testid="contact-service-select"
              className="rounded-none border-slate-300 h-11 focus:ring-[#0284C7]"
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem
                  key={s.slug}
                  value={s.title}
                  data-testid={`contact-service-option-${s.slug}`}
                >
                  {s.title}
                </SelectItem>
              ))}
              <SelectItem value="Other" data-testid="contact-service-option-other">
                Other / Not sure
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="contact-message" className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569]">
          Message <span className="text-[#DC2626]">*</span>
        </Label>
        <Textarea
          id="contact-message"
          data-testid="contact-message-input"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Tell us what's going on..."
          rows={5}
          className="rounded-none border-slate-300 focus-visible:ring-[#0284C7] resize-none"
          required
        />
      </div>

      <Button
        type="submit"
        data-testid="contact-form-submit-button"
        disabled={loading}
        className="w-full rounded-none bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold text-base py-6"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="h-4 w-4 mr-2" strokeWidth={1.75} />
            Send Message
          </>
        )}
      </Button>

      <p className="text-xs text-slate-500 text-center">
        We respond as quickly as possible. For emergencies, please call us
        directly.
      </p>
    </form>
  );
}
